import { DateTime } from "luxon";
import type { SharedApiV2 } from "@capstone/mock-api";
import { Day } from "src/app/core/day.model";
import { Site, SiteReference } from "src/app/core/sites";
import { TimeOfDay } from "src/app/core/time-of-day.model";
import { Weekdays } from "src/app/core/weekdays.model";
import { getApiDetailsDecorator } from "src/utils";
import { DockReference, getDockRouteUrl } from "./dock.model";
import { formatHourlyCapacityName } from "./hourly-dock-capacity.model";

const api = getApiDetailsDecorator<SharedApiV2.DockHourlyCapacityDto>();

abstract class BaseHourlyDockCapacityV2 {
  protected constructor(args: ClassProperties<BaseHourlyDockCapacityV2>) {
    this.appointment = args.appointment;
    this.case = args.case;
    this.endTime = args.endTime;
    this.id = args.id;
    this.pallet = args.pallet;
    this.site = args.site;
    this.startTime = args.startTime;
    this.weekdays = args.weekdays;
  }

  @api({ key: "appointmentCapacity" }) public readonly appointment:
    number | null;
  @api({ key: "caseCapacity" }) public readonly case: number | null;
  @api({ key: "concreteEndTime" }) public readonly endTime: TimeOfDay;
  @api({ key: "palletCapacity" }) public readonly pallet: number | null;
  @api() public readonly startTime: TimeOfDay;
  @api({ key: "dayOfWeek" }) public readonly weekdays: Weekdays;
  @api() public readonly id: number;
  public readonly site: Site;
}

export class HourlyDockCapacityV2 extends BaseHourlyDockCapacityV2 {
  private constructor(
    args: Omit<ClassProperties<HourlyDockCapacityV2>, "name">,
  ) {
    super(args);

    // Computed properties
    this.name = formatHourlyCapacityName(this);
  }

  // Computed properties
  public readonly name: string;

  public static deserialize(
    data: SharedApiV2.DockHourlyCapacityDto,
    { site }: DeserializeArguments,
  ): HourlyDockCapacityV2 | HourlyDockCapacityExceptionV2 {
    return data.precedence > 0
      ? HourlyDockCapacityExceptionV2.deserialize(data, { site })
      : new HourlyDockCapacityV2({
          appointment: data.appointmentCapacity ?? null,
          case: data.caseCapacity ?? null,
          endTime: TimeOfDay.deserialize(data.concreteEndTime),
          id: data.id,
          pallet: data.palletCapacity ?? null,
          site,
          startTime: TimeOfDay.deserialize(data.startTime),
          weekdays: Weekdays.deserialize(data.dayOfWeek),
        });
  }

  public isApplicableAt(time: DateTime): boolean {
    return (
      this.weekdays.isSelected(time) &&
      new Day(time)
        .toInterval(time.zone, this.startTime, this.endTime)
        .contains(time)
    );
  }
}

export class HourlyDockCapacityExceptionV2 extends BaseHourlyDockCapacityV2 {
  private constructor(
    args: Omit<ClassProperties<HourlyDockCapacityExceptionV2>, "name">,
  ) {
    super(args);
    this.customName = args.customName;
    this.endDay = args.endDay;
    this.precedence = args.precedence;
    this.startDay = args.startDay;

    // Computed properties
    this.name = this.customName || formatHourlyCapacityName(this);
  }

  @api({ key: "endDate" }) public readonly endDay: Day;
  @api({ key: "name" }) public readonly customName: string | null;
  @api({ key: "startDate" }) public readonly startDay: Day;
  @api() public readonly precedence: number;

  // Computed properties
  public readonly name: string;
  public static deserialize(
    data: SharedApiV2.DockHourlyCapacityDto,
    { site }: DeserializeArguments,
  ): HourlyDockCapacityExceptionV2 {
    if (!data.startDate) {
      throw new Error("Start date required for hourly dock capacity.");
    }
    if (!data.endDate) {
      throw new Error("End date required for hourly dock capacity.");
    }

    return new HourlyDockCapacityExceptionV2({
      appointment: data.appointmentCapacity ?? null,
      case: data.caseCapacity ?? null,
      customName: data.name ?? null,
      // TODO: update the deserialization to use the site time zone once implemented in the API
      // We deserialize the date to UTC as API ignores the site time zone.
      endDay: Day.deserialize(data.endDate, Day.utc),
      endTime: TimeOfDay.deserialize(data.concreteEndTime),
      id: data.id,
      pallet: data.palletCapacity ?? null,
      precedence: data.precedence,
      site,
      // TODO: update the deserialization to use the site time zone once implemented in the API
      // We deserialize the date to UTC as API ignores the site time zone.
      startDay: Day.deserialize(data.startDate, Day.utc),
      startTime: TimeOfDay.deserialize(data.startTime),
      weekdays: Weekdays.deserialize(data.dayOfWeek),
    });
  }

  public isApplicableAt(time: DateTime): boolean {
    const businessDay = this.site.getBusinessDay(time).day;

    return (
      this.weekdays.isSelected(businessDay) &&
      new Day(time)
        .toInterval(time.zone, this.startTime, this.endTime)
        .contains(time) &&
      businessDay.isAfterOrSameDay(this.startDay) &&
      !businessDay.isAfter(this.endDay)
    );
  }
}

interface DeserializeArguments {
  readonly site: Site;
}

export function getHourlyCapacitiesExceptionUrl(
  site: SiteReference,
  dock: DockReference,
  currentHourCapacitiesException: Pick<HourlyDockCapacityExceptionV2, "id">,
): string {
  return getDockRouteUrl(
    site,
    dock,
    "hourly-capacities/exceptions",
    String(currentHourCapacitiesException.id),
  );
}
